/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {githubDarkTheme, JsonEditor} from 'json-edit-react';
import {translate} from '@ion/core/src/locale/locale';
import {OfflineCanRequestResult} from '@ion/core/src/types/services';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {dlsColor} from '@ion/dls/src/theme/theme';

export interface CanRequestJsonViewProps {
  requestDetails?: OfflineCanRequestResult | null;
  onBack: () => void;
}

const useStyles = makeStyles(() => ({
  root : {
    width         : '100%',
    display       : 'flex',
    flexDirection : 'column',
    flex          : 1,
    minWidth      : 0,
    minHeight     : 0,
    overflow      : 'hidden'
  },
  breadcrumbBack : {
    margin     : '12px 0px',
    minHeight  : '32px',
    maxHeight  : '32px',
    flexShrink : 0,
    '&:hover'  : {
      cursor : 'default'
    },
    '& span' : {
      color : dlsColor('grey3')
    }
  },
  breadcrumbBackLink : {
    color     : `${dlsColor('grey2')} !important`,
    cursor    : 'pointer',
    '&:hover' : {
      color : `${dlsColor('black')} !important`
    }
  },
  breadcrumbCurrent : {
    color         : `${dlsColor('grey3')} !important`,
    cursor        : 'default',
    pointerEvents : 'none'
  },
  breadcrumbChevron : {
    color : dlsColor('grey3')
  },
  content : {
    width         : '100%',
    display       : 'flex',
    flexDirection : 'column',
    flex          : 1,
    minWidth      : 0,
    minHeight     : 0,
    overflow      : 'hidden',
    borderRadius  : '4px'
  },
  editorHost : {
    width     : '100%',
    display   : 'flex',
    flex      : 1,
    minWidth  : 0,
    minHeight : 0,
    overflow  : 'hidden',
    '& > *'   : {
      display   : 'flex',
      flex      : 1,
      minWidth  : 0,
      minHeight : 0
    },
    '& .jer-editor-container' : {
      boxSizing : 'border-box',
      width     : '100% !important',
      minWidth  : '0 !important',
      maxWidth  : '100% !important',
      height    : '100%',
      minHeight : 0,
      overflow  : 'auto'
    }
  }
}));

/**
 * Displays a read-only JSON editor for the selected CAN request details payload.
 *
 * ```typescript
 * <CanRequestJsonView
 *   requestDetails={requestDetails}
 *   onBack={onBack}
 * />
 * ```
 *
 * @param   requestDetails Selected CAN request details shown in the JSON editor.
 * @param   onBack         Invoked when the user navigates back to CAN requests.
 * @returns The CAN request JSON view component.
 */
const CanRequestJsonView = ({
  requestDetails,
  onBack
}: CanRequestJsonViewProps) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Breadcrumb height='32px'
        breadcrumbClass={classes.breadcrumbBack}
        iconClick={onBack}
        icon={<ChevronLeft className={classes.breadcrumbChevron} />}>
        <span className={classes.breadcrumbBackLink}
          onClick={onBack}>
          {translate('vehicle-manager:vehicleHistoryGrid.canRequestsTab')}
        </span>
        <span className={classes.breadcrumbCurrent}>
          {translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.menu.viewJson')}
        </span>
      </Breadcrumb>
      <div className={classes.content}>
        <div className={classes.editorHost}>
          <JsonEditor data={requestDetails || {}}
            theme={githubDarkTheme}
            minWidth='100%'
            maxWidth='100%'
            restrictEdit={function() { return true; }}
            restrictTypeSelection
            restrictDelete
            restrictAdd
            rootName='' />
        </div>
      </div>
    </div>
  );
};

export default CanRequestJsonView;
